/**
 * guide/vote 가이드 단위 도움됨 투표 island.
 *
 * Mount: `<section data-mount="guide-vote" data-service-slug="..." data-guide-slug="...">`
 *   contains `<button data-rating="up|down">` and `<span data-up-count>` /
 *   `<span data-down-count>` for live counter updates.
 *   Optional: `<div data-vote-status>` to receive a visible status message
 *   (e.g. "서버 오류" on 5xx, "잠시 후" on network) so silent failures
 *   don't repeat the PR2 incident where 500 from missing migration was
 *   invisible to the user.
 *
 * Anon ID: `getOrCreateAnonId()` (LocalStorage UUID v4).
 */

import { getOrCreateAnonId } from "./anonymous-id";

type Bound = { serviceSlug: string; guideSlug: string; root: HTMLElement };

function readBinding(el: HTMLElement): Bound | null {
  const serviceSlug = el.dataset.serviceSlug;
  const guideSlug = el.dataset.guideSlug;
  if (!serviceSlug || !guideSlug) return null;
  return { serviceSlug, guideSlug, root: el };
}

function writeCounts(root: HTMLElement, up: number, down: number): void {
  const upEl = root.querySelector<HTMLElement>("[data-up-count]");
  const downEl = root.querySelector<HTMLElement>("[data-down-count]");
  if (upEl) upEl.textContent = String(up);
  if (downEl) downEl.textContent = String(down);
}

function setStatus(root: HTMLElement, text: string): void {
  const target = root.querySelector<HTMLElement>("[data-vote-status]");
  if (target) target.textContent = text;
}

/** Refresh DOM counts from server cache columns. SSG-baked values are
 * 0/0 (catalog JSON has no cache fields), so first paint is wrong until
 * this fetch completes. Silent on failure — SSG fallback stays. */
async function refreshCountsFromServer(bound: Bound): Promise<void> {
  const url = `/api/guide-votes?service_slug=${encodeURIComponent(bound.serviceSlug)}&guide_slug=${encodeURIComponent(bound.guideSlug)}`;
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) return;
  const data = (await res.json()) as { up_count: number; down_count: number };
  writeCounts(bound.root, data.up_count, data.down_count);
}

export function mountGuideVote(root: HTMLElement): void {
  const bound = readBinding(root);
  if (!bound) return;

  // Fire-and-forget mount-time refresh. Don't block click binding.
  void refreshCountsFromServer(bound).catch(() => {
    /* silent */
  });

  for (const btn of root.querySelectorAll<HTMLButtonElement>("button[data-rating]")) {
    const rating = btn.dataset.rating === "down" ? "down" : "up";
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      setStatus(bound.root, "");
      try {
        const res = await fetch("/api/guide-votes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            anon_id: getOrCreateAnonId(),
            service_slug: bound.serviceSlug,
            guide_slug: bound.guideSlug,
            rating,
          }),
        });
        if (res.status === 429) {
          setStatus(bound.root, "잠시 후 다시 시도해주세요 (rate-limit)");
          return;
        }
        if (!res.ok) {
          setStatus(bound.root, `서버 오류 (${res.status}) — 잠시 후 다시 시도해주세요`);
          return;
        }
        const data = (await res.json()) as {
          inserted: boolean;
          up_count: number;
          down_count: number;
        };
        writeCounts(bound.root, data.up_count, data.down_count);
        setStatus(bound.root, data.inserted ? "고맙습니다 ✓" : "이미 이번 주에 투표하셨습니다");
      } catch {
        setStatus(bound.root, "네트워크 오류 — 잠시 후 다시 시도해주세요");
      } finally {
        btn.disabled = false;
      }
    });
  }
}
